<template>
    <ui-container class="py-8" v-if="this.form">
        <ui-header
            :title="editMode ? 'Editing Daily Briefing - '+this.dailyBriefing.formattedDate : 'Create new Daily Briefing'"
            :subtitle="editMode ? 'ID #'+this.dailyBriefing.id : null">
            <template #actions>
                <ui-button-new icon="fas fa-arrow-left" @click="finished()" v-if="editMode">Done</ui-button-new>
            </template>
        </ui-header>
        <div class="grid gap-6 grid-cols-12 py-6">
            <ui-date-picker label="Daily Briefing Date" name="date" v-model="form.date" class="xl:col-span-6"
                            placeholder="What is date for this induction?"
                            :error="errors.date" :disabled="dailyBriefing.locked"
                            @input="save"></ui-date-picker>
            <ui-input label="Location of Works" name="location" v-model="form.locationOfWorks"
                      placeholder="Main roof area, etc." :disabled="dailyBriefing.locked"
                      class="xl:col-span-6" @input="save"></ui-input>
            <ui-textarea autoresize label="Key Activities" name="keyActivities" v-model="form.keyActivities"
                         placeholder="Eg a description of works being carried out, plant or equipment being used, any overlap with other works etc."
                         class="xl:col-span-12" @input="save" :disabled="dailyBriefing.locked"></ui-textarea>
            <ui-textarea autoresize label="Key Hazards" name="keyHazards" v-model="form.keyHazards"
                         placeholder="Eg deliveries, weather, permitted works, exclusion zones, excavations, traffic routes, etc."
                         class="xl:col-span-12" @input="save" :disabled="dailyBriefing.locked"></ui-textarea>
        </div>
        <label class="my-0 py-0 mt-0">Attendees</label>
        <div
            class="flex mt-1.5 border-t border-b border-gray-200 bg-gray-50 text-xs text-gray-500 font-semibold draggable">
            <div class="w-6 flex-shrink-0"></div>
            <div class="flex-1 border-l border-r border-gray-200 px-2 py-1">Name</div>
            <div class="flex-1 border-r border-gray-200 px-2 py-1">Position</div>
            <div class="w-20 border-r border-gray-200 px-2 py-1">Signature</div>
            <div class="w-6 flex-shrink-0 ml-2">
            </div>
        </div>
        <draggable :list="currentAttendees" draggable=".draggable" handle=".handle" group="attendees" @change="save">
            <daily-briefings-attendee v-for="attendee in currentAttendees"
                                      :key="attendee.id"
                                      :attendee="attendee"
                                      ref="attendees"
                                      :daily-briefing="dailyBriefing"
                                      :contacts="contacts"
                                      @updatedAttendee="save"
                                      @removeAttendee="removeAttendee"
            ></daily-briefings-attendee>
        </draggable>
        <!-- New Task Item. -->
        <div class="flex border-b -mb-px border-gray-200">
            <div class="w-6 flex-shrink-0">
                <div class="px-2 py-2 w-5 h-5 flex attendees-center">
                    <i class="fa fa-plus text-xs text-gray-300"></i>
                </div>
            </div>
            <div class="flex-1 border-l border-r border-gray-200 px-0 py-0">
                <textarea
                    placeholder="Add a new attendee..."
                    class="px-2 py-1 b-0 border-0 hover:z-10 focus:z-20 resize-none text-sm m-0 border-none shadow-none relative"
                    type="text"
                    rows="1"
                    v-model="newAttendee.name"
                    @input="addAttendeeLine"
                ></textarea>
            </div>
            <div class="w-6 ml-2 mt-1">
            </div>
        </div>
        <div class="grid gap-6 grid-cols-12 py-6">
            <package-contact-search
                class="col-span-12 xl:col-span-6"
                #default="{ open, query, setQuery }"
                @selected:contact="givenBySelected"
                :top="true"
                :contacts="contacts"
            >
                <ui-input label="Given By" name="givenByName" v-model="form.givenByName" placeholder=""
                          class="w-full" :disabled="dailyBriefing.locked"
                          @input="setQuery(form.givenByName);open();save()"
                          @focus="setQuery(form.givenByName);open();"
                ></ui-input>
            </package-contact-search>
            <ui-input label="Position" name="givenByPosition" v-model="form.givenByPosition" placeholder=""
                      class="col-span-12 xl:col-span-6" @input="save" :disabled="dailyBriefing.locked"></ui-input>
        </div>

        <ui-data-grid-item wide class="mt-6 flex" v-if="!editMode">
            <div class="flex flex-col md:flex-row justify-end flex-wrap">
                <ui-button-new v-if="editMode" @click="edit(false)" class="ml-3">Cancel</ui-button-new>
                <ui-button-new primary @click.prevent="form.submit()" :loading="form.busy" class="ml-3">Create
                </ui-button-new>
            </div>
        </ui-data-grid-item>

        <ui-data-grid class="mt-6" v-if="editMode">
            <ui-data-grid-item label="Created By">{{ dailyBriefing.createdBy }} on {{
                    dailyBriefing.createdOn
                }}
            </ui-data-grid-item>
            <ui-data-grid-item label="Last Updated By" v-if="dailyBriefing.lastUpdatedBy">{{
                    dailyBriefing.lastUpdatedBy
                }} on {{
                    dailyBriefing.lastUpdatedOn
                }}
            </ui-data-grid-item>
        </ui-data-grid>

    </ui-container>

</template>

<script>
import {DailyBriefing} from '@models'
import UiInput from "../../ui/UiInput.vue";
import draggable from 'vuedraggable'
import UiContainer from "../../ui/UiContainer.vue";
import UiDatePicker from "../../ui/UiDatePicker.vue";
import UiButtonNew from "../../ui/UiButtonNew.vue";
import AutoresizableTextarea from "../../ui/AutoresizableTextarea";
import UiDataGrid from "../../ui/UiDataGrid.vue";
import UiDataGridItem from "../../ui/UiDataGridItem.vue";
import UiDropdownItem from "../../ui/UiDropdownItem.vue";
import UiDropdown from "../../ui/UiDropdown.vue";
import PackageContactSearch from "../../contacts/PackageContactSearch.vue";
import UiTextarea from "../../ui/UiTextarea.vue";
import UiHeader from "../../ui/UiHeader.vue";
import DailyBriefingsAttendee from "./DailyBriefingsAttendee.vue";
import {uuid} from "../../../utils";
import moment from "moment";

export default {
    components: {
        UiHeader,
        UiTextarea, DailyBriefingsAttendee,
        PackageContactSearch,
        UiDropdown,
        UiDropdownItem,
        UiDataGridItem,
        UiDataGrid,
        AutoresizableTextarea,
        UiButtonNew,
        UiDatePicker,
        UiContainer,
        draggable,
        UiInput,

    },
    props: {
        item: Object,
        edit: Function,
        addItem: Function,
        contacts: Object,
        packageReference: null,
        packageName: null,
    },
    data() {
        const initialData = this.item ? this.item.getFormAttributes() : {
            date: moment().format('YYYY-MM-DD'),
            attendees: []
        };
        return {
            currentAttendees: [],
            newAttendee: {
                name: null
            },
            dailyBriefing: null,
            form: null,
            errors: {
                date: null
            },
            saving: false,
            saveAgain: false,
        }
    },
    methods: {
        addAttendeeLine() {
            if (!this.newAttendee.name) return
            this.currentAttendees.push({
                id: uuid(),
                name: this.newAttendee.name,
                position: '',
            });
            this.save();
            this.newAttendee.name = ''
            this.$nextTick(() => {
                const inputField = this.$refs.attendees[this.$refs.attendees.length - 1].$refs.name
                let val = inputField.value;
                inputField.focus();
                inputField.value = val;
            })
        },
        removeAttendee(e) {
            this.currentAttendees = this.currentAttendees.filter(attendee => attendee.id !== e.id);
            this.save();
        },
        save() {
            if (this.editMode) {
                if (this.saving) {
                    this.saveAgain = true;
                }
                this.saving = true
                this.form.submit()
            }
        },
        finished() {
            let sorter = (items) => {
                items.sort((a, b) => (a.date < b.date) ? 1 : ((b.date < a.date) ? -1 : 0));
            };
            this.edit(false, sorter)
        },
        givenBySelected(e) {
            this.form.givenByName = e.name
            this.save();
        },
    },
    computed: {
        editMode() {
            return !!this.item
        },
    },
    async mounted() {
        if (this.item) {
            this.dailyBriefing = await DailyBriefing.find(this.item.packageReference, this.item.id)
            this.currentAttendees = this.dailyBriefing.attendees
        } else {
            this.dailyBriefing = {date: moment().format('YYYY-MM-DD'), attendees: []}
        }
        this.form = new Form(this.editMode ? this.dailyBriefing.getFormAttributes() : this.dailyBriefing, async (form) => {
            this.saving = true;
            do {
                this.saveAgain = false
                let data = form.export();
                if (!data.date) {
                    this.errors.date = "The date field is required."
                    return;
                }
                data.packageReference = this.packageReference;
                data.packageName = this.packageName;
                data.attendees = this.currentAttendees ? this.currentAttendees : [];
                data.attendees.map((p, i) => p.order = i);
                let sorter = (items) => {
                    items.sort((a, b) => (a.date < b.date) ? 1 : ((b.date < a.date) ? -1 : 0));
                };
                if (this.editMode) {
                    await this.dailyBriefing.update(data).then(() =>
                        Object.assign(this.item, this.dailyBriefing)
                    )
                    this.edit(true, sorter)
                } else {
                    const induction = await DailyBriefing.create(this.packageReference, uuid(), data)

                    this.addItem(induction, 0, sorter);
                    form.reset()
                }
            } while (this.saveAgain)
            this.saving = false;
        })
    }
}
</script>
